import React, { useRef, useState } from 'react'
import { MultiSelectOption, MultiSelectProps } from './MultiSelect.types'
import styles from './MultiSelect.module.scss'
import cl from 'classnames'
import Select, { MultiValueProps } from 'react-select'
import { customStyles } from './MultiSelect.styles'
import { Tag } from '../Tag'
import { ChangeMarkPoint } from '../ChangeMarkPoint'
import { ObjectValueStatus } from 'store'
import { Button } from 'components'
import { IconCheckboxOFF, IconCheckboxON, IconMinusCircle, IconPlusCircle } from 'assets'
import { Text1 } from '../Text1'

const MultiValue = (props: MultiValueProps<MultiSelectOption<any>>) => {
  const {
    data: { label, versionChange },
    removeProps
  } = props

  return (
    <div>
      <Tag
        tag={label}
        removable
        onRemove={() => removeProps.onClick(null)}
        className={cl({
          [styles.tagMarked]: versionChange,
          [styles.lineThrough]: versionChange?.valueStatus === ObjectValueStatus.REMOVED
        })}
      >
        {versionChange && (
          <ChangeMarkPoint
            style={{ bottom: 0, textTransform: 'none' }}
            {...versionChange}
          />
        )}
      </Tag>
    </div>
  )
}

const CustomOption = (props) => {
  const { data, onOptionClick, value, expandedItems, handleExpandClick } = props
  const isSelected = Array.isArray(value) && value.includes(data.value)

  const isExpanded = expandedItems[data.value]

  const handleCheckboxClick = (event) => {
    event.stopPropagation()
    onOptionClick(data)
  }

  return (
    <div>
      <div onClick={handleCheckboxClick} className={styles.customOption}>
        <Button className={styles.checkbox}>
          { isSelected
            ? <IconCheckboxON />
            : <IconCheckboxOFF />}
        </Button>

        {data.children && data.children.length > 0 && (
          !isExpanded
            ? <IconPlusCircle style={{ cursor: 'pointer' }} fontSize={20} onClick={(e) => handleExpandClick(e, data.value)}></IconPlusCircle>
            : <IconMinusCircle style={{ cursor: 'pointer' }} fontSize={20} onClick={(e) => handleExpandClick(e, data.value)}></IconMinusCircle>
        )}
        <Text1 className={styles.bold} style={{ cursor: 'pointer' }}>{data.label}</Text1>
      </div>
      {isExpanded && data.children && (
        <div style={{ paddingLeft: '32px' }}>
          {data.children.map((child, index) => (
            <CustomOption {...props} key={index} data={child} isExpandedProp={expandedItems[child.value]} />
          ))}
        </div>
      )}
    </div>
  )
}
function ClearIndicator (props) {
  return (
    <svg width='20' height='20' viewBox='0 0 20 20' onClick={props.clearAllSelectedValues}>
      {/* Circle */}
      <circle cx='10' cy='10' r='8' fill='lightgray' />

      {/* X mark */}
      <path
        d='M7,7 L13,13 M13,7 L7,13'
        stroke='white'
        strokeWidth='1'
      />
    </svg>
  )
}

function MultiSelectComponent<ValueType = string> (props: MultiSelectProps<ValueType>) {
  const {
    options,
    value,
    onChange,
    maxSelections,
    placeholder,
    closeMenuOnSelect,
    style
  } = props

  const [expandedItems, setExpandedItems] = useState({})
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const selectRef = useRef<any>(null)

  const handleMouseEnter = () => {
    setMenuIsOpen(true)
  }

  const handleMouseLeave = () => {
    setMenuIsOpen(false)
  }

  const handleExpandClick = (event, optionValue) => {
    event.stopPropagation()
    setExpandedItems(prevState => ({
      ...prevState,
      [optionValue]: !prevState[optionValue]
    }))
  }

  const handleChange = (selectedOptions: MultiSelectOption<ValueType>[]) => {
    if (!onChange) return

    const selections = selectedOptions || []

    if (maxSelections && selections.length > maxSelections) return

    onChange(selections.map(o => o.value))
  }

  const handleOptionClick = (option) => {
    let newSelectedOptions
    if (Array.isArray(value) && value.includes(option.value)) {
      newSelectedOptions = value.filter(selectedValue => selectedValue !== option.value)
    } else {
      newSelectedOptions = [...(Array.isArray(value) ? value : []), option.value]
    }

    if (onChange) {
      onChange(newSelectedOptions)
    }
  }

  function CustomOptionWrapper (props) {
    return <CustomOption
      {...props} value={value}
      onOptionClick={handleOptionClick}
      expandedItems={expandedItems}
      handleExpandClick={handleExpandClick}
    // eslint-disable-next-line react/jsx-closing-bracket-location
    />
  }

  const getSelectedOptions = (options, value) => {
    if (!Array.isArray(value)) return []

    return options.reduce((acc, option) => {
      if (value.includes(option.value) || option.versionChange) {
        acc.push(option)
      }
      if (option.children) {
        acc.push(...getSelectedOptions(option.children, value))
      }
      return acc
    }, [])
  }

  const clearAllSelectedValues = () => {
    if (onChange) {
      onChange([])
    }
  }

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ width: '400px', ...style }}>
      <Select
        isMulti
        ref={selectRef}
        menuIsOpen={menuIsOpen}
        placeholder={placeholder}
        options={options}
        value={getSelectedOptions(options, value)}
        styles={customStyles}
        components={{
          Option: CustomOptionWrapper,
          MultiValue,
          ClearIndicator: () => <ClearIndicator clearAllSelectedValues={clearAllSelectedValues} />
        }}
        onChange={handleChange}
        closeMenuOnSelect={closeMenuOnSelect === undefined
          ? maxSelections
            ? (Array.isArray(value) && value.length === maxSelections - 1)
            : true
          : closeMenuOnSelect}
        hideSelectedOptions={false}
      />
    </div>
  )
}

const MultiSelect = MultiSelectComponent
export { MultiSelect }
